import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarClient, durationMinutes, localDay, localTime,
  type Calendar as CalendarInfo, type CalendarEvent,
} from "../lib/calendar";
import { Icon } from "./Icon";

/**
 * Month view, using the design's .calwrap / .cal / .cal-grid / .cal-c / .ev.
 *
 * Dates are handled without timezone conversion where it matters. A JSCalendar
 * `start` is a *local* wall-clock string with the zone carried separately, so
 * `new Date("2026-07-23T10:00:00")` would reinterpret it in the browser's zone
 * and drift an event across a day boundary for anyone not sitting in the
 * calendar's timezone. Bucketing is done on the "YYYY-MM-DD" prefix instead.
 */

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Local YYYY-MM-DD, not toISOString() — that shifts to UTC and can move a day. */
function dayKey(date: Date): string {
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

/** The 6×7 grid of days covering `month`, starting Monday. */
function gridDays(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  // getDay() is 0=Sunday; shift so Monday is 0.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function endLabel(event: CalendarEvent): string {
  const minutes = durationMinutes(event.duration);
  if (!minutes) return localTime(event.start);
  const [h, m] = localTime(event.start).split(":").map(Number);
  const total = h * 60 + m + minutes;
  const eh = `${Math.floor(total / 60) % 24}`.padStart(2, "0");
  const em = `${total % 60}`.padStart(2, "0");
  return `${localTime(event.start)}–${eh}:${em}`;
}

export function CalendarView({ client }: { client: CalendarClient }) {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [calendars, setCalendars] = useState<CalendarInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [time, setTime] = useState("09:00");

  const days = useMemo(() => gridDays(month), [month]);
  const today = dayKey(new Date());

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cals, list] = await Promise.all([
        client.calendars(),
        // Query the whole visible grid, not just the month, so events in the
        // leading and trailing days render rather than appearing to vanish.
        client.events(days[0], new Date(days[41].getTime() + 86_400_000)),
      ]);
      setCalendars(cals);
      setEvents(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the calendar");
    }
  }, [client, days]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Events bucketed by local day, each day sorted by start time. */
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const key = localDay(event.start);
      const bucket = map.get(key);
      if (bucket) bucket.push(event);
      else map.set(key, [event]);
    }
    for (const bucket of map.values()) bucket.sort((a, b) => a.start.localeCompare(b.start));
    return map;
  }, [events]);

  async function add(day: string) {
    const calendar = calendars.find((c) => c.isDefault) ?? calendars[0];
    if (!calendar || !title.trim()) return;
    try {
      await client.createEvent({
        calendarId: calendar.id,
        title: title.trim(),
        start: `${day}T${time}:00`,
        duration: "PT1H",
        timeZone: calendar.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setTitle("");
      setAdding(null);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add the event");
    }
  }

  async function remove(event: CalendarEvent) {
    try {
      await client.deleteEvent(event.id);
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the event");
    }
  }

  function shift(months: number) {
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() + months, 1));
  }

  return (
    <section className="calwrap" data-screen-label="Calendar">
      <div className="cal-h">
        <h2>{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</h2>
        <button className="ibtn" title="Previous month" onClick={() => shift(-1)}>
          <Icon name="arrow-left" />
        </button>
        <button className="ibtn" title="Next month" onClick={() => shift(1)} style={{ transform: "rotate(180deg)" }}>
          <Icon name="arrow-left" />
        </button>
        <button
          className="today"
          onClick={() => {
            const now = new Date();
            setMonth(new Date(now.getFullYear(), now.getMonth(), 1));
          }}
        >
          Today
        </button>
        <button className="ibtn" title="Refresh" onClick={() => void load()} style={{ marginLeft: "auto" }}>
          <Icon name="refresh-cw" />
        </button>
      </div>

      {error && (
        <div role="alert" style={{ color: "var(--bad)", font: "400 12.5px/1.4 Inter", padding: "10px 22px" }}>
          {error}
        </div>
      )}

      <div className="cal">
        <div className="cal-dow">
          {DOW.map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="cal-grid">
          {days.map((date) => {
            const key = dayKey(date);
            const dayEvents = byDay.get(key) ?? [];
            const offMonth = date.getMonth() !== month.getMonth();
            return (
              <div
                key={key}
                className={`cal-c${offMonth ? " off" : ""}${key === today ? " today" : ""}`}
                onDoubleClick={() => setAdding(key)}
                title="Double-click to add an event"
              >
                <div className="d">{date.getDate()}</div>
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    className="ev"
                    title={`${endLabel(event)} — ${event.title ?? "Untitled"}${event.description ? `\n${event.description}` : ""}\n\nDouble-click to delete`}
                    onDoubleClick={(e) => { e.stopPropagation(); void remove(event); }}
                  >
                    {event.showWithoutTime ? "" : `${localTime(event.start)} `}
                    {event.title ?? "Untitled"}
                  </div>
                ))}
                {adding === key && (
                  <div onDoubleClick={(e) => e.stopPropagation()}>
                    <input
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      type="time"
                      style={{ width: "100%", marginBottom: 4, background: "var(--pane2)", border: "1px solid var(--line)", borderRadius: 6, color: "var(--tx)", font: "500 10.5px/1.3 Inter", padding: "3px 5px" }}
                    />
                    <input
                      autoFocus
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void add(key);
                        if (e.key === "Escape") { setAdding(null); setTitle(""); }
                      }}
                      onBlur={() => { if (!title.trim()) setAdding(null); }}
                      placeholder="Event title"
                      style={{ width: "100%", background: "var(--pane2)", border: "1px solid var(--line-ind)", borderRadius: 6, color: "var(--tx)", font: "500 10.5px/1.3 Inter", padding: "4px 6px" }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
